<template>
    <div class="dash">
      <h1 class="topper">{{ userTown }} Happenings</h1>
      
      <!-- Month Navigation -->
      <div class="date-navigation">
        <img src="@/assets/left arrow icon.svg" alt="Previous Month" class="nav-arrow" @click="previousMonth" :class="{ disabled: isMinMonth }"/>
        <span class="current-date">{{ formattedMonth }}</span>
        <img src="@/assets/arrow icon.svg" alt="Next Month" class="nav-arrow" @click="nextMonth" :class="{ disabled: isMaxMonth }"/>
      </div>
      
      <!-- Meetings Container -->
      <div class="cards-container" v-if="userTown === 'Goshen'" @click="$router.push('/yearinreview')">
        <div class="article-card">
            <p class="cardhead">Goshen: Year in Review</p>
            <p class="cardsubhead">Pinned Post</p>
            <p class="cardtags">Prepare for Town Meeting on March 3rd by reviewing what happened at the Selectboard in the last year.</p>
        </div>
      </div>


      <div v-if="loading" class="loading">Loading meetings...</div>
      <div v-else-if="error" class="error">{{ error }}</div>
      <div v-else-if="filteredMeetings.length === 0" class="no-meetings">No meetings available yet for this month. Use the left arrow above to see last month's meetings.</div>
      <div v-else class="cards-container">
        <div
          v-for="meeting in filteredMeetings" :key="meeting.id" class="article-card" @click="openMeetingDetails(meeting)">
          <p class="cardhead">
            {{ (meeting.type === 'Minutes Only' || meeting.type === 'Upcoming Meeting')
              ? `${meeting.type}: ${meeting.name}`
              : meeting.type }}
          </p>
          <p class="cardsubhead"> {{formatDate(meeting.date)}}</p>
          <p class="cardtags" v-if="!['Upcoming Meeting', 'Minutes Only'].includes(meeting.type)"> Topics:
              <span v-if="meeting?.tags && meeting?.tags.length > 0">
                {{ meeting?.tags.join(', ') }}
              </span>
              <span v-else>
                Currently Not Available
              </span>
          </p>
          <p v-if="'Upcoming Meeting'.includes(meeting.type)">Click here for the agenda.</p>
          <p v-if="'Minutes Only'.includes(meeting.type)">Click here for the minutes.</p>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { collection, query, orderBy, getDocs, where, doc, getDoc } from 'firebase/firestore';
  import { db } from '@/firebase';
  
  export default {
    name: 'TownInfo',
    props: {
      settings: {
        type: Object,
        required: true
      },
      userTown: {
        type: String,
        default: 'Loading',
      },
    },
    data() {
      return {
        meetings: [],
        currentMonth: new Date(),
        loading: false,
        error: null,
        minMonth: new Date('2024-02-01'),
        maxMonth: new Date(),
        townMeetingPull: []
      };
    },

    watch: {
      userTown(newTown, oldTown) {
        if (newTown !== oldTown && newTown !== 'Loading') {
          this.fetchTownMapping();
          this.fetchMeetings();
        }
      }
    },
    computed: {
      filteredMeetings() {
          return this.meetings.filter(meeting => {
          // Map meeting types to settings properties
          //console.log('Meeting type:', meeting.type);
          const typeToSetting = {
            'Select Board Meeting': 'selectBoard',
            'School Board Meeting': 'schoolBoard',
            'Town Meeting': 'otherTownMeetings',
            'Commission or Sub-Group': 'commissionGroup',
            'Upcoming Meeting': 'upcomingMeetings',       // New mapping
            'Minutes Only': 'minutesOnly'    // New mapping
          };
  
          // Get the corresponding setting for this meeting type
          const settingName = typeToSetting[meeting.type];
          const passesSetting = !settingName || this.settings[settingName];
          const passesTown = this.townMeetingPull.length
            ? this.townMeetingPull.includes(meeting.town)
            : true;
          //console.log('Town:', meeting.town, 'Passes town:', passesTown);

          // If there's no corresponding setting or the setting is true, show the meeting
          return passesSetting && passesTown;
      })},

      formattedMonth() {
        const months = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'June', 'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.'];
        const month = months[this.currentMonth.getMonth()];
        const year = this.currentMonth.getFullYear();
        return `${month} ${year}`;
      },

      isMaxMonth() {
        const now = new Date();
        return new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth()) >= new Date(now.getFullYear(), now.getMonth());
      },

      isMinMonth() {
        if (!this.minMonth) return false;
        return new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth()) <= 
               new Date(this.minMonth.getFullYear(), this.minMonth.getMonth());
      },
    },

    mounted() {
      this.init();
      },
      
      methods: {
      async fetchTownMapping() {
        try {
          const mappingDoc = await getDoc(doc(db, 'townMapping', this.userTown));
          if (mappingDoc.exists()) {
            this.townMeetingPull = mappingDoc.data().meetingPull || [];
          } else {
            console.error('No town mapping found for', this.userTown);
          }
        } catch (err) {
          console.error('Error fetching town mapping:', err);
        }
      },

      async init() {
        if (this.userTown && this.userTown !== 'Loading') {
          await this.fetchTownMapping();
        }
        await this.fetchMeetings();
      },
      
      async fetchMeetings() {
        this.loading = true;
        this.error = null;
        try {
          const startDate = `${this.currentMonth.getFullYear()}-${String(this.currentMonth.getMonth() + 1).padStart(2, '0')}-01`;
          const lastDay = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 0);
          const endDate = `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;
          
          const meetingsRef = collection(db, 'meetingInfo');
          const q = query(
            meetingsRef,
            where('date', '>=', startDate),
            where('date', '<=', endDate),
            orderBy('date', 'desc')
          );
  
          const querySnapshot = await getDocs(q);
          this.meetings = querySnapshot.docs.map(doc => {
            const data = doc.data();
            // Force the date to be interpreted at noon UTC to avoid timezone issues
            const date = new Date(`${data.date}T12:00:00Z`);
            
            return {
              id: doc.id,
              ...data,
              date
            };
          });
        } catch (error) {
          console.error('Error fetching meetings:', error);
          this.error = 'Failed to load meetings. Please try again later.';
        } finally {
          this.loading = false;
        }
      },

      formatDate(date) {
        //defensive practice for bad dates
        if (!(date instanceof Date)) {
          console.error('Invalid date object:', date);
          return 'Invalid Date';
        }
        //format full date from database
        return date.toLocaleDateString('en-US',{ year: 'numeric', month: 'short', day: 'numeric'}).replace(/(\w+) /, '$1. ');
      },

      openMeetingDetails(meeting) {
        if (meeting.type === 'Upcoming Meeting' && meeting.files?.other) {
          this.openDocument('other', meeting);
        } else if (meeting.type === 'Minutes Only' && meeting.files?.minutes) {
          this.openDocument('minutes', meeting);
        } else {
          this.$router.push(`/view/${meeting.id}`);
        }
      },

      openDocument(type, meeting) {
        const fileUrl = meeting?.files?.[type];
        if (fileUrl) {
          window.open(fileUrl, '_blank');
        }
      },

      previousMonth() {
        if (!this.isMinMonth) {
          this.currentMonth = new Date(this.currentMonth.setMonth(this.currentMonth.getMonth() - 1));
          this.fetchMeetings();
        }
      },

      nextMonth() {
        if (!this.isMaxMonth) {
          this.currentMonth = new Date(this.currentMonth.setMonth(this.currentMonth.getMonth() + 1));
          this.fetchMeetings();
        }
      },
    },
  };
  </script>
  
  <style scoped>
  .dash {
    background-color: #fffff8;
    padding: 0px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  .topper {
    text-align: center;
    padding-top: 64px;
    margin-bottom: 10px;
  }
  
  /* Date Navigation Styles */
  .date-navigation {
    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: 0px;
  }
  
  .nav-arrow {
    width: 14px;
    height: 14px;
    cursor: pointer;
    padding-top: 10px;
    padding-bottom: 10px;
    padding-left: 8px;
    padding-right: 8px;
    box-sizing: content-box;
    color: #545051;
  }
  
  .nav-arrow.disabled {
    opacity: 0.2;
    cursor: not-allowed;
  }
  
  .current-date {
    font-size: 16px;
    font:inherit;
    color: #545051;
  }
  
  /* Card Styles */
  
  .cards-container {
    width: 80%;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  .article-card {
    width: 100%;
    margin-top: 20px;
    background-color: #fffff8;
    border: 1px solid #545051;
    border-radius: 20px;
    padding: 10px;
    cursor: pointer;
  }

  .cardhead {
    font: inherit;
    font-weight: 600;
    font-size: 14pt;
    color: #545051;
    margin: 0rem;
    padding-bottom: 0rem;
  }

  .cardsubhead{
    font-size: 1rem;
    font-family: 'IBM Plex Serif', serif;
    color:#545051;
    line-height: 14pt;
    padding-bottom: 14pt;
  }

  
  .article-card:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
  
  .loading,
  .error,
  .no-meetings {
    text-align: left;
    margin: 20px 20px;
    font:inherit;
    color: #545051;
  }
  
  .error {
    color: red;
  }

  @media (max-width: 767px) {
  .topper {
    display: none;
  }
}
  </style>